import { BadgeCheck, Leaf, ShieldCheck, Users, MessageSquareText } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { assetUrl } from "@/lib/api";
import { Eyebrow, Reveal, SectionTitle } from "./Primitives";
import { Cta } from "./Cta";

const POINT_ICONS = [Leaf, ShieldCheck, Users, MessageSquareText];

// Shown only when the admin enables a real expert profile with a name.
export const Expert = () => {
  const { content } = useSite();
  const ex = content.expert || {};
  if (!ex.enabled || !ex.name) return null;
  return (
    <section id="expert" data-testid="expert-section" className="bg-[#EEF4EE] py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <div className="relative mx-auto max-w-[460px]">
            <div className="absolute -inset-3 rounded-[40px] border border-gold/60" aria-hidden />
            <div className="relative aspect-[760/807] overflow-hidden rounded-[32px] bg-sand shadow-[0_40px_80px_-35px_rgba(13,59,46,0.55)]">
              {ex.image && <img src={assetUrl(ex.image)} alt={`${ex.name}, ${ex.title}`} loading="lazy" decoding="async" width="760" height="807" data-testid="expert-photo" className="h-full w-full object-cover" />}
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            {ex.eyebrow && <Eyebrow>{ex.eyebrow}</Eyebrow>}
            {ex.heading && <SectionTitle>{ex.heading}</SectionTitle>}
            {ex.bio && <p className="t-body mt-5 max-w-xl text-sage">{ex.bio}</p>}
            <div data-testid="expert-profile" className="mt-7 rounded-3xl bg-white p-6 ring-1 ring-forest/10 sm:p-7">
              <p data-testid="expert-name" className="text-2xl font-extrabold tracking-[-0.01em] text-forest sm:text-[28px]">{ex.name}</p>
              {ex.title && <p data-testid="expert-title" className="t-strong mt-1 text-leaf">{ex.title}</p>}
              {ex.highlights?.length > 0 && (
                <ul className="mt-5 space-y-3">
                  {ex.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 t-body font-medium text-ink">
                      <BadgeCheck className="mt-1 h-5 w-5 shrink-0 text-leaf" aria-hidden />{h}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Reveal>
          {ex.points?.length > 0 && (
            <ul className="mt-6 grid gap-4 sm:grid-cols-2">
              {ex.points.map((pt, i) => {
                const Icon = POINT_ICONS[i % POINT_ICONS.length];
                return (
                  <li key={pt.title}>
                    <Reveal delay={(i % 2) * 0.06} className="flex h-full gap-4 rounded-2xl bg-white/70 p-5 ring-1 ring-forest/10">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-sand text-leaf"><Icon className="h-5 w-5" aria-hidden /></span>
                      <span>
                        <span className="block text-[17px] font-bold text-forest">{pt.title}</span>
                        <span className="mt-1 block text-[15px] leading-relaxed text-sage">{pt.text}</span>
                      </span>
                    </Reveal>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-8 max-w-md">
            <Cta kind="consult" name="expert_section_consultation" position="expert" className="w-full" />
          </div>
        </div>
      </div>
    </section>
  );
};
